/**
 * BackToTop — floating "return to top" button that appears after the user
 * scrolls past 600 px. Hidden on routes where it would fight a bottom-fixed
 * CTA bar (auth / in-app pages). Smooth animated entry & hover nudge.
 */

import React from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

const SHOW_AFTER_PX = 600;

// Paths where we hide the button to avoid clashing with fixed app UI
// (sticky CTAs, chat composers, dashboard toolbars, etc.).
const HIDDEN_PATH_PREFIXES = [
  "/login",
  "/onboarding",
  "/dashboard",
  "/listings",
  "/inbox",
  "/settings",
  "/admin",
];

export default function BackToTop() {
  const { pathname } = useLocation();
  const [visible, setVisible] = React.useState(false);

  const hidden = HIDDEN_PATH_PREFIXES.some((p) => pathname.startsWith(p));

  React.useEffect(() => {
    if (hidden) {
      setVisible(false);
      return undefined;
    }
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setVisible(window.scrollY > SHOW_AFTER_PX);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hidden, pathname]);

  if (hidden) return null;

  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Back to top"
      data-testid="back-to-top-btn"
      className={[
        "fixed right-5 z-40",
        // Stacked above the AI launcher — mobile bottom-24 + 4.5rem, desktop bottom-6 + 4.5rem.
        "bottom-[9.5rem] sm:bottom-[5.5rem]",
        "h-11 w-11 rounded-full",
        "bg-slate-900 text-white dark:bg-white dark:text-slate-900",
        "shadow-[0_8px_30px_-12px_rgba(15,23,42,0.4)]",
        // 60 % transparence au repos — same discreet pattern as the AI bubble.
        "opacity-40 hover:opacity-100 focus-visible:opacity-100",
        "hover:-translate-y-0.5 hover:shadow-[0_12px_36px_-12px_rgba(15,23,42,0.45)]",
        "transition-all duration-300 ease-out",
        "flex items-center justify-center",
        visible
          ? "translate-y-0 pointer-events-auto"
          : "!opacity-0 translate-y-2 pointer-events-none",
      ].join(" ")}
    >
      <ArrowUp className="w-5 h-5" strokeWidth={2} />
    </button>
  );
}
